import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, CircularProgress, FormControlLabel, Switch, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import GameService from '../../services/game.service';
import { MenuProps } from '../newcoach/components/common';
import { getPlayerTagId } from '../../common/tagIdentity';
import '../newcoach/coach_style.css';

const courtAreaList = [
    { name: 'Defensive', id: 4 },
    { name: 'Defensive Middle', id: 3 },
    { name: 'Offensive Middle', id: 2 },
    { name: 'Offensive', id: 1 }
];

const PlayerTagEditRequestDialog = ({ open, onClose, player, cur_player_id, t, applyImmediately = false }) => {
    const [playerList, setPlayerList] = useState([]);
    const [actionList, setActionList] = useState([]);
    const [actionTypeList, setActionTypeList] = useState([]);
    const [actionResultList, setActionResultList] = useState([]);
    const [loading, setLoading] = useState(false); // State for loading indicator
    const [pendingRequest, setPendingRequest] = useState(null); // State for pending correction
    // Some callers (e.g. Multiple_Video_Screens rows) carry no player_id, so the
    // clip's current player is resolved from its name instead.
    const [originalPlayerId, setOriginalPlayerId] = useState(null);
    const [errorMsg, setErrorMsg] = useState(''); // Surface submit failures instead of closing silently
    const [values, setValues] = useState({
        player_name: null,
        action_name: null,
        action_type: null,
        action_result: null,
        court_area: null,
        inside_pain: false,
        start_time: '00:00:00',
        end_time: '00:00:00'
    });

    const handleChange = (prop) => (e) => {
        setValues({ ...values, [prop]: e.target.value });
    };

    const handleTagSaveRequest = () => {
        setErrorMsg('');
        setLoading(true); // Set loading to true while processing
        const reqPlayerId = values.player_name?.player_id;
        const playerTagId = getPlayerTagId(player);

        // Corrections.player_tag_id is NOT NULL; without an id the request
        // would fail in the database with an opaque constraint error.
        if (playerTagId === null) {
            setLoading(false);
            setErrorMsg(t('This clip is missing its tag reference, so it cannot be corrected.'));
            return;
        }

        // Corrections.old_player_id is NOT NULL too.
        const curPlayerId = cur_player_id ?? originalPlayerId;

        if (curPlayerId === null || curPlayerId === undefined) {
            setLoading(false);
            setErrorMsg(t('This clip is missing its player reference, so it cannot be corrected.'));
            return;
        }

        const requestData = {
            curPlayerId,
            newPlayerId: reqPlayerId,
            playerTagId,
            actionId: values.action_name?.id,
            actionTypeId: values.action_type?.id,
            actionResultId: values.action_result?.id,
            courtAreaId: values.court_area?.id,
            insidePain: values.inside_pain,
            startTime: values.start_time,
            endTime: values.end_time
        };
        // Managers write the tag directly. Filing a request as well would queue
        // an approval for a change that has already been applied.
        const submit = applyImmediately
            ? GameService.updatePlayerTagByManual({
                  id: playerTagId,
                  player_id: reqPlayerId,
                  action: values.action_name?.id,
                  action_type: values.action_type?.id,
                  action_result: values.action_result?.id,
                  court_area: values.court_area?.id,
                  inside: values.inside_pain,
                  start: values.start_time,
                  end: values.end_time
              })
            : GameService.addCorrectionRequest(requestData);

        submit
            .then(() => {
                setLoading(false);
                onClose(true);
            })
            .catch((err) => {
                setLoading(false);
                // Keep the dialog open and tell the user why, instead of closing silently
                setErrorMsg(err?.response?.data?.message || err?.message || 'Failed to submit the correction request. Please try again.');
            });
    };

    useEffect(() => {
        (async () => {
            if (!player) return;
            setErrorMsg('');
            setLoading(true);
            let players = [];
            let actions = [];
            let types = [];
            let results = [];
            let pending = null;

            try {
                // Check for pending request
                await GameService.getPendingCorrectionByTag(getPlayerTagId(player) ?? '')
                    .then((res) => {
                        if (res && res.length > 0) {
                            pending = res[0];
                            setPendingRequest(res[0]);
                        } else {
                            setPendingRequest(null);
                        }
                    })
                    .catch(() => setPendingRequest(null));

                await GameService.getAllActions().then((res) => { setActionList(res); actions = res; }).catch(() => {});
                await GameService.getAllActionTypes().then((res) => { setActionTypeList(res); types = res; }).catch(() => {});
                await GameService.getAllActionResults().then((res) => { setActionResultList(res); results = res; }).catch(() => {});
                await GameService.getGameTeamPlayersByTeam(player.team_id, player.game_id).then((res) => { setPlayerList(res); players = res; }).catch(() => {});

                // If API calls failed and we have tag data, create single-item fallback
                if (actions.length === 0 && player.action_id) {
                    actions = [{ id: player.action_id, name: player.action_name }];
                    setActionList(actions);
                }
                if (types.length === 0 && player.action_type_id) {
                    types = [{ id: player.action_type_id, name: player.action_type }];
                    setActionTypeList(types);
                }
                if (results.length === 0 && player.action_result_id) {
                    results = [{ id: player.action_result_id, name: player.action_result }];
                    setActionResultList(results);
                }
                if (players.length === 0 && player.player_id) {
                    players = [{ player_id: player.player_id, player_full_name: player.player_name }];
                    setPlayerList(players);
                }

                // If pending request exists, populate with pending data instead of current
                if (pending) {
                    setOriginalPlayerId(pending.old_player_id ?? null);
                    setValues((prev) => ({
                        ...prev,
                        player_name: players.find((p) => p.player_id === pending.new_player_id) || null,
                        court_area: courtAreaList.find((c) => c.id === pending.requested_court_area_id) || null,
                        action_name: actions.find((a) => a.id === pending.requested_action_id) || null,
                        action_type: types.find((t) => t.id === pending.requested_action_type_id) || null,
                        action_result: results.find((r) => r.id === pending.requested_action_result_id) || null,
                        inside_pain: pending.requested_inside_pain,
                        start_time: pending.requested_start_time,
                        end_time: pending.requested_end_time
                    }));
                } else {
                    // Match by name, tolerant of casing/extra whitespace (e.g. names stored with a trailing space)
                    const norm = (s) => (s || '').toString().trim().toLowerCase();
                    // Use API data if available, otherwise fall back to tag IDs
                    const actionObj = actions.find((a) => norm(a.name) === norm(player.action_name)) ||
                        (player.action_id ? { id: player.action_id, name: player.action_name } : null);
                    const typeObj = types.find((t) => norm(t.name) === norm(player.action_type)) ||
                        (player.action_type_id ? { id: player.action_type_id, name: player.action_type } : null);
                    const resultObj = results.find((r) => norm(r.name) === norm(player.action_result)) ||
                        (player.action_result_id ? { id: player.action_result_id, name: player.action_result } : null);
                    const playerObj = players.find((p) => norm(p.player_full_name) === norm(player.player_name)) ||
                        (player.player_id ? { player_id: player.player_id, player_full_name: player.player_name } : null);

                    setOriginalPlayerId(playerObj?.player_id ?? null);
                    setValues((prev) => ({
                        ...prev,
                        player_name: playerObj,
                        court_area: courtAreaList.find((c) => c.id === player.court_area) || null,
                        action_name: actionObj,
                        action_type: typeObj,
                        action_result: resultObj,
                        inside_pain: player.inside_pain,
                        start_time: player.start_time,
                        end_time: player.end_time
                    }));
                }
            } catch (error) {
                console.error('Error loading tag edit data:', error);
            } finally {
                setLoading(false);
            }
        })();
    }, [player]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg">
            <DialogTitle>{pendingRequest ? t('Pending Request - Awaiting Approval') : t('Request Tag Change')}</DialogTitle>
            <DialogContent
                dividers
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    justifyContent: loading ? 'center' : 'flex-start', // Center content if loading
                    alignItems: loading ? 'center' : 'stretch', // Center spinner horizontally
                    height: '300px', // Fixed height for proper centering
                }}
            >
                {loading ? (
                    // Centered spinner
                    <CircularProgress />
                ) : pendingRequest ? (
                    // Show pending request details (read-only)
                    <div style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '12px' }}>{t('You have already requested changes for this tag. Please wait for coach approval.')}</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <p><strong>{t('Requested Player')}:</strong> {values.player_name?.player_full_name || 'N/A'}</p>
                            <p><strong>{t('Requested Action')}:</strong> {values.action_name?.name || 'N/A'}</p>
                            <p><strong>{t('Requested Type')}:</strong> {values.action_type?.name || 'N/A'}</p>
                            <p><strong>{t('Requested Result')}:</strong> {values.action_result?.name || 'N/A'}</p>
                            <p><strong>{t('Requested Court Area')}:</strong> {values.court_area?.name || 'N/A'}</p>
                            <p><strong>{t('Inside Box')}:</strong> {values.inside_pain ? t('Yes') : t('No')}</p>
                            <p><strong>{t('Start Time')}:</strong> {values.start_time}</p>
                            <p><strong>{t('End Time')}:</strong> {values.end_time}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {errorMsg && (
                            <div style={{ padding: '10px 12px', backgroundColor: '#fdecea', color: '#b71c1c', borderRadius: '8px', fontSize: '13px' }}>
                                {errorMsg}
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <div>
                                <p className="normal-text" style={{ paddingLeft: '12px' }}>
                                    {t('Players')}
                                </p>
                                <Select
                                    value={values.player_name}
                                    onChange={handleChange('player_name')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ borderRadius: '10px', outline: 'none', height: '36px', width: '200px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    {playerList.map((item) => (
                                        <MenuItem key={item.player_id} value={item}>
                                            {item.player_full_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <p className="normal-text" style={{ paddingLeft: '12px' }}>
                                    {t('Court Areas')}
                                </p>
                                <Select
                                    value={values.court_area}
                                    onChange={handleChange('court_area')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ borderRadius: '10px', outline: 'none', height: '36px', width: '200px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    {courtAreaList.map((item, index) => (
                                        <MenuItem key={index} value={item}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <FormControlLabel
                                control={<Switch checked={values.inside_pain} onChange={(e) => setValues({ ...values, inside_pain: e.target.checked })} />}
                                label={t('In the box')}
                                sx={{ mt: 1 }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <div>
                                <p className="normal-text" style={{ paddingLeft: '12px' }}>
                                    {t('Actions')}
                                </p>
                                <Select
                                    value={values.action_name}
                                    onChange={handleChange('action_name')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ borderRadius: '10px', outline: 'none', height: '36px', width: '200px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    {actionList.map((item) => (
                                        <MenuItem key={item.id} value={item}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <p className="normal-text" style={{ paddingLeft: '12px' }}>
                                    {t('Action Types')}
                                </p>
                                <Select
                                    value={values.action_type}
                                    onChange={handleChange('action_type')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ borderRadius: '10px', outline: 'none', height: '36px', width: '200px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    {actionTypeList.map((item) => (
                                        <MenuItem key={item.id} value={item}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <p className="normal-text" style={{ paddingLeft: '12px' }}>
                                    {t('Action Results')}
                                </p>
                                <Select
                                    value={values.action_result}
                                    onChange={handleChange('action_result')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ borderRadius: '10px', outline: 'none', height: '36px', width: '200px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    {actionResultList.map((item) => (
                                        <MenuItem key={item.id} value={item}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                            <TextField
                                value={values.start_time}
                                onChange={handleChange('start_time')}
                                placeholder="00:00:00"
                                label={t('Start Time')}
                                variant="outlined"
                                sx={{ width: '240px', fontSize: '0.8rem', '& .MuiOutlinedInput-root': { borderRadius: '10px' }, '& .MuiOutlinedInput-input': { padding: '0 12px', height: '36px' } }}
                            />
                            <TextField
                                value={values.end_time}
                                onChange={handleChange('end_time')}
                                placeholder="00:00:00"
                                label={t('End Time')}
                                variant="outlined"
                                sx={{ width: '240px', fontSize: '0.8rem', '& .MuiOutlinedInput-root': { borderRadius: '10px' }, '& .MuiOutlinedInput-input': { padding: '0 12px', height: '36px' } }}
                            />
                        </div>
                    </>
                )}
            </DialogContent>
            {!loading && (
                <DialogActions>
                    <Button variant="outlined" onClick={() => onClose(false)}>
                        {pendingRequest ? t('Close') : t('Cancel')}
                    </Button>
                    {!pendingRequest && (
                        <Button
                            variant="outlined"
                            onClick={() => handleTagSaveRequest()}
                            disabled={
                                !values.player_name ||
                                !values.action_name ||
                                !values.start_time ||
                                !values.end_time
                            }
                        >
                            {t('Request')}
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default PlayerTagEditRequestDialog;
